import React, {useEffect, useMemo, useState} from 'react'
import {Button, Card, Form, Input} from 'antd'
import AccountApi from '../../../Api/AccountApi'
import AccountDomain from '../../../Domain/AccountDomain'
import {NotificationCommon} from '../../../Component/Notification'

const PAYMENT_PROVIDER_BANKS = ['9pay', 'gpay', 'sepay']

type FieldConfig = {
    key: string
    label: string
    secret?: boolean
    required?: boolean
    placeholder?: string
}

const FIELD_CONFIG: Record<string, FieldConfig[]> = {
    '9pay': [
        {key: 'merchantKey', label: 'Merchant Key', required: true},
        {key: 'secretKey', label: 'Secret Key', secret: true, required: true},
        {key: 'checksumKey', label: 'Checksum Key', secret: true, required: true},
        {key: 'autoApprover', label: 'Auto approver', placeholder: '9pay'},
        {key: 'autoReviewer', label: 'Auto reviewer', placeholder: '9pay'},
    ],
    gpay: [
        {key: 'merchantCode', label: 'Merchant Code', required: true},
        {key: 'password', label: 'Password', secret: true, required: true},
        {key: 'privateKey', label: 'Private Key (RSA PEM)', secret: true, required: true},
        {key: 'bankCode', label: 'Bank Code', placeholder: 'BIDV'},
    ],
    sepay: [
        {key: 'sepayBankName', label: 'Tên ngân hàng (SePay QR)', required: true, placeholder: 'Vietcombank'},
        {key: 'webhookSecret', label: 'Webhook Secret (HMAC)', secret: true, required: true},
        {key: 'autoApprover', label: 'Auto approver', placeholder: 'sepay'},
        {key: 'autoReviewer', label: 'Auto reviewer', placeholder: 'sepay'},
    ],
}

const EMPTY_FIELDS: FieldConfig[] = []

interface Props {
    account: AccountDomain
    onSaved?: () => void
}

export const IntegrationSettings: React.FC<Props> = ({account, onSaved}) => {
    const [form] = Form.useForm()
    const [saving, setSaving] = useState(false)

    const bankCode = useMemo(() => {
        const bank = account?.bank as any
        if (!bank) return ''
        return typeof bank === 'string' ? bank : bank.code
    }, [account])

    const fields = useMemo(() => FIELD_CONFIG[bankCode] ?? EMPTY_FIELDS, [bankCode])
    const visible = PAYMENT_PROVIDER_BANKS.includes(bankCode)

    useEffect(() => {
        if (!visible) return
        const settings = account.integrationSettings || {}
        const initialValues: Record<string, string> = {}
        fields.forEach((field) => {
            const value = settings[field.key]
            initialValues[field.key] = value != null ? String(value) : ''
        })
        form.setFieldsValue(initialValues)
    }, [account, bankCode, fields, form, visible])

    const handleSave = () => {
        form.validateFields()
            .then((values) => {
                const integrationSettings: Record<string, string> = {}
                fields.forEach((field) => {
                    const raw = values[field.key]
                    if (raw != null && String(raw).trim() !== '') {
                        integrationSettings[field.key] = String(raw).trim()
                    }
                })
                setSaving(true)
                return AccountApi.integrationSettingsConfigure(account.code, {integrationSettings})
            })
            .then(() => {
                NotificationCommon.success({message: 'Lưu cấu hình tích hợp thành công'})
                onSaved?.()
            })
            .catch((error) => {
                if (error?.errorFields) {
                    return
                }
                NotificationCommon.error({message: 'Lưu cấu hình tích hợp thất bại'})
            })
            .finally(() => setSaving(false))
    }

    if (!visible) {
        return null
    }

    return (
        <Card className="account-info mg-bt-12" title="Cấu hình tích hợp thanh toán">
            <Form form={form} layout="vertical" className="integration-settings-form">
                {fields.map((field) => (
                    <Form.Item
                        key={field.key}
                        name={field.key}
                        label={field.label}
                        rules={field.required ? [{required: true, message: `${field.label} không được để trống`}] : []}>
                        {field.secret ? (
                            <Input.Password placeholder={field.placeholder || `Nhập ${field.label.toLowerCase()}`} />
                        ) : (
                            <Input placeholder={field.placeholder || `Nhập ${field.label.toLowerCase()}`} />
                        )}
                    </Form.Item>
                ))}
            </Form>
            <Button type="primary" loading={saving} onClick={handleSave}>
                <i className="fa-solid fa-check mg-r-6" />
                Lưu cấu hình
            </Button>
        </Card>
    )
}

export default IntegrationSettings
